/* eslint-disable no-unused-vars */
// Alarm player — generates tones via Web Audio API (no binary assets).
// Also handles settings (global + per-prescription override) and optional vibration.

const TONES = {
  chime: {
    label: "Chime",
    notes: [
      { freq: 523.25, dur: 0.2, type: "sine" }, // C5
      { freq: 659.25, dur: 0.4, type: "sine" }, // E5
    ],
    gap: 0.9, // seconds between loops
  },
  bell: {
    label: "Bell",
    notes: [
      { freq: 880, dur: 0.6, type: "sine", decay: true },
    ],
    gap: 1.3,
  },
  gentle: {
    label: "Gentle",
    notes: [
      { freq: 392, dur: 0.35, type: "sine" }, // G4
      { freq: 440, dur: 0.35, type: "sine" }, // A4
      { freq: 493.88, dur: 0.6, type: "sine" }, // B4
    ],
    gap: 1.8,
  },
  urgent: {
    label: "Urgent",
    notes: [
      { freq: 1046.5, dur: 0.12, type: "square" }, // C6
      { freq: 784, dur: 0.12, type: "square" }, // G5
      { freq: 1046.5, dur: 0.12, type: "square" },
      { freq: 784, dur: 0.12, type: "square" },
    ],
    gap: 0.5,
  },
};

export const TONE_LIST = Object.entries(TONES).map(([id, t]) => ({ id, label: t.label }));

const GLOBAL_KEY = "verihandi_alarm_global";
const PER_RX_KEY = "verihandi_alarm_per_rx";

export function getGlobalAlarm() {
  try {
    const raw = localStorage.getItem(GLOBAL_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { enabled: true, tone: "chime", volume: 0.6, vibrate: true, ringOnOpen: true };
}
export function setGlobalAlarm(s) {
  localStorage.setItem(GLOBAL_KEY, JSON.stringify({ ...getGlobalAlarm(), ...s }));
}

function getPerRxMap() {
  try { return JSON.parse(localStorage.getItem(PER_RX_KEY) || "{}"); } catch { return {}; }
}
export function getRxAlarmOverride(rxId) {
  return getPerRxMap()[rxId] || null;
}
export function setRxAlarmOverride(rxId, s) {
  const map = getPerRxMap();
  if (s === null) {
    delete map[rxId];
  } else {
    map[rxId] = { ...(map[rxId] || {}), ...s };
  }
  localStorage.setItem(PER_RX_KEY, JSON.stringify(map));
}
export function resolveAlarm(rxId) {
  const g = getGlobalAlarm();
  const o = rxId ? getRxAlarmOverride(rxId) : null;
  if (!o) return g;
  return { ...g, ...o };
}

// ---- Audio engine ----
let audioCtx = null;
let activeStop = null;

function ensureCtx() {
  if (audioCtx && audioCtx.state !== "closed") return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  return audioCtx;
}

async function unlock() {
  const ctx = ensureCtx();
  if (!ctx) return false;
  if (ctx.state === "suspended") {
    try { await ctx.resume(); } catch { /* ignore */ }
  }
  return ctx.state === "running";
}

function playNote(ctx, note, startAt, volume) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = note.type || "sine";
  osc.frequency.setValueAtTime(note.freq, startAt);
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(volume, startAt + 0.02);
  if (note.decay) {
    // exponential decay for bell-like sounds
    gain.gain.exponentialRampToValueAtTime(Math.max(0.001, volume * 0.01), startAt + note.dur);
  } else {
    gain.gain.setValueAtTime(volume, startAt + note.dur - 0.05);
    gain.gain.linearRampToValueAtTime(0, startAt + note.dur);
  }
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + note.dur + 0.05);
}

async function playLoop(toneId, volume, maxSeconds = 45) {
  const ok = await unlock();
  if (!ok) return () => {};
  const tone = TONES[toneId] || TONES.chime;
  const ctx = ensureCtx();
  let stopped = false;
  const start = ctx.currentTime + 0.05;

  function scheduleCycle(from) {
    if (stopped) return;
    let cursor = from;
    for (const n of tone.notes) {
      playNote(ctx, n, cursor, volume);
      cursor += n.dur;
    }
    const nextCycle = cursor + tone.gap;
    const elapsed = nextCycle - start;
    if (elapsed < maxSeconds) {
      const ms = (nextCycle - ctx.currentTime) * 1000;
      setTimeout(() => scheduleCycle(nextCycle), Math.max(0, ms));
    }
  }
  scheduleCycle(start);

  return () => { stopped = true; };
}

function vibrateIfSupported(pattern = [300, 150, 300]) {
  try {
    if ("vibrate" in navigator) {
      navigator.vibrate(pattern);
      // repeat up to 5 times to match audio loop duration
      let reps = 0;
      const iv = setInterval(() => {
        if (++reps >= 5) { clearInterval(iv); return; }
        try { navigator.vibrate(pattern); } catch { /* ignore */ }
      }, 2000);
      return () => { clearInterval(iv); try { navigator.vibrate(0); } catch { /* ignore */ } };
    }
  } catch { /* ignore */ }
  return () => {};
}

/** Play the alarm for a prescription. Returns stop() fn. */
export async function playAlarm(rxId, { preview = false } = {}) {
  stopAlarm();
  const cfg = resolveAlarm(rxId);
  if (!cfg.enabled) return () => {};
  const maxSec = preview ? 2.5 : 45;
  const stopAudio = await playLoop(cfg.tone, cfg.volume, maxSec);
  const stopVibe = !preview && cfg.vibrate ? vibrateIfSupported() : () => {};
  activeStop = () => { try { stopAudio(); } catch { /* ignore */ } try { stopVibe(); } catch { /* ignore */ } };
  if (preview) {
    setTimeout(() => stopAlarm(), maxSec * 1000 + 200);
  }
  return activeStop;
}

export function stopAlarm() {
  if (activeStop) {
    try { activeStop(); } catch { /* ignore */ }
    activeStop = null;
  }
}
